import { makeObservable } from 'mobx';

import { concatPath, moment, TimeFormat } from '@feathr/hooks';
import type { IBaseAttributes, TConstraints } from '@feathr/rachis';
import { Collection, Model } from '@feathr/rachis';

import type { ITTDCampaignAttributes } from './campaigns/ttd_base';

export enum ERecommendationClass {
  Budget = 'Recommendation.Budget',
  BudgetDuration = 'Recommendation.BudgetDuration',
  ExtendCampaign = 'Recommendation.ExtendCampaign',
  MissingGoal = 'Recommendation.MissingGoal',
}

export enum ERecommendationType {
  Budget = 'Budget',
  BudgetDuration = 'BudgetDuration',
  ExtendCampaign = 'ExtendCampaign',
  MissingGoal = 'MissingGoal',
  // TODO: these are placeholder for now - update when andrew has added these types
  UpdateCreatives = 'UpdateCreatives',
  IncreaseBaseBid = 'IncreaseBaseBid',
  IncreaseFrequencyCap = 'IncreaseFrequencyCap',
  EnableOmnitargeting = 'EnableOmnitargeting',
}

export enum ERecommendationStatus {
  /** The recommendation has been applied to the campaign  */
  Applied = 'applied',
  /** The recommendation was automatically dismissed due to the campaign reaching its end date or conflicting changes were made in the wizard */
  AutoDismissed = 'auto_dismissed',
  /** The recommendation was dismissed by the user */
  Dismissed = 'dismissed',
  /** The recommendation has been created but not sent to the user */
  Pending = 'pending',
  /** The recommendation is visible and the user should receive a notification and email with the recommendation */
  Active = 'active',
}

export interface IRecommendation extends IBaseAttributes {
  _cls: ERecommendationClass;
  account: string;
  campaign: string;
  date_created: string;
  title: string;
  message: string;
  status: ERecommendationStatus;
  type: ERecommendationType;
  current_campaign_values: Partial<ITTDCampaignAttributes>;
  proposed_campaign_values: Partial<ITTDCampaignAttributes>;
  roi: number;
}

export class Recommendation extends Model<IRecommendation> {
  public readonly className = 'Recommendation';

  public get constraints(): TConstraints<IRecommendation> {
    return {};
  }

  public static readonly defaults: Partial<IRecommendation> = {
    date_created: moment.utc().format(TimeFormat.isoDateTime),
  };

  public override getDefaults(): Partial<IRecommendation> {
    return {
      ...super.getDefaults(),
      ...Recommendation.defaults,
    };
  }

  constructor(attributes: Partial<IRecommendation> = {}) {
    super(attributes);

    makeObservable(this);
  }

  public getItemUrl(pathSuffix?: string): string {
    return concatPath(`/recommendations/${this.id}`, pathSuffix);
  }
}

export class Recommendations extends Collection<Recommendation> {
  public getClassName(): string {
    return 'recommendations';
  }

  public override url(): string {
    return `${this.getHostname()}recommendations/`;
  }

  public getModel(attributes: Partial<IRecommendation>): Recommendation {
    return new Recommendation(attributes);
  }
}
